import { ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTimezone } from "@/contexts/timezone-context";
import type { CiRunDetail, CiTestResult } from "@/lib/types";
import { cn } from "@/lib/utils";
import {
  formatDurationMs,
  NODE_TIMELINE_COLORS,
  type NodeTimelineLaneRole,
  nodeTimelineBarColor,
} from "./timeline-shared";

/**
 * Cryod variant of MachineTimeline — lanes are keyed by cluster node name
 * (from cluster_info) rather than the machine-pool machine_name. Each test
 * bar is placed on the nodes it actually used (source + restore for
 * cross_restore, all restore_nodes for multi_restore).
 */

interface TestBar {
  result: CiTestResult;
  leftPercent: number;
  widthPercent: number;
  startMs: number;
  laneRole: NodeTimelineLaneRole;
}

interface NodeLane {
  nodeName: string;
  bars: TestBar[];
}

/**
 * Resolve ``node1``/``node2`` aliases (from older runs written before the
 * publisher resolved them at ingest) to the real hostname using the ordered
 * ``clusterNodeNames`` list. Keeps unknown names as-is.
 */
function resolveNodeAlias(name: string, clusterNodeNames: string[]): string {
  // Don't rewrite a value that's already a real cluster hostname — guards
  // against the (unlikely) case where a host is literally named "node1".
  if (clusterNodeNames.includes(name)) return name;
  const match = /^node(\d+)$/.exec(name);
  const indexCapture = match?.[1];
  if (!indexCapture) return name;
  const index = Number.parseInt(indexCapture, 10) - 1;
  return clusterNodeNames[index] ?? name;
}

/**
 * Sentinel lane key for tests that don't specify source/restore/multi.
 * They run on whichever node K3s schedules them on — which we don't
 * capture — so we route them to a single dedicated lane rather than
 * drawing duplicate bars on every hostname lane.
 */
const UNASSIGNED_LANE = "__unassigned__";
const UNASSIGNED_LANE_LABEL = "any node";

function nodesForResult(result: CiTestResult, clusterNodeNames: string[]): string[] {
  const touched = new Set<string>();
  if (result.nodes?.source) touched.add(resolveNodeAlias(result.nodes.source, clusterNodeNames));
  if (result.nodes?.restore) touched.add(resolveNodeAlias(result.nodes.restore, clusterNodeNames));
  for (const node of result.nodes?.multi ?? [])
    touched.add(resolveNodeAlias(node, clusterNodeNames));
  if (touched.size === 0) {
    // Cluster-wide tests (webhook-mutation, checkpoint-restore-cpu, etc.)
    // ran on one unspecified node. Group them onto a single lane so the
    // timeline doesn't imply they ran on every node.
    touched.add(UNASSIGNED_LANE);
  }
  return Array.from(touched);
}

function isCrossNodeResult(result: CiTestResult): boolean {
  const source = result.nodes?.source;
  const restore = result.nodes?.restore;
  const multi = result.nodes?.multi ?? [];
  return !!source && !!restore && source !== restore && multi.length === 0;
}

/**
 * For a cross-node test, split the wall-clock window into two bars:
 *   source lane: [start, start + (total - restore))    — checkpoint phase
 *   restore lane: [start + (total - restore), start + total] — restore phase
 *
 * `total_restore_ms` is the wall-clock from restore-pod apply to Ready on the
 * restore node, so `total - total_restore_ms` is the earlier phase on the
 * source node (ready + checkpoint + mooncake push). Same-node restores and
 * multi-restore fall back to a single bar on every lane.
 */
function barRangeForNode(
  result: CiTestResult,
  nodeName: string,
  startMs: number,
  durationMs: number,
  clusterNodeNames: string[],
): { startMs: number; durationMs: number; laneRole: NodeTimelineLaneRole } {
  if (!isCrossNodeResult(result)) {
    return { startMs, durationMs, laneRole: "single" };
  }
  const source = result.nodes?.source
    ? resolveNodeAlias(result.nodes.source, clusterNodeNames)
    : undefined;
  const restore = result.nodes?.restore
    ? resolveNodeAlias(result.nodes.restore, clusterNodeNames)
    : undefined;
  const restoreMs = result.total_restore_ms ?? 0;
  if (restoreMs <= 0 || restoreMs >= durationMs) {
    // Can't split — keep same range on both lanes, label by role so
    // colors still differentiate even though widths don't.
    const role: NodeTimelineLaneRole = nodeName === source ? "source" : "restore";
    return { startMs, durationMs, laneRole: role };
  }
  const checkpointMs = durationMs - restoreMs;
  if (nodeName === source) {
    return { startMs, durationMs: checkpointMs, laneRole: "source" };
  }
  if (nodeName === restore) {
    return { startMs: startMs + checkpointMs, durationMs: restoreMs, laneRole: "restore" };
  }
  return { startMs, durationMs, laneRole: "single" };
}

interface BuiltLanes {
  lanes: NodeLane[];
  totalMs: number;
  firstTestStartMs: number;
  hasCrossNode: boolean;
}

function buildLanes(run: CiRunDetail): BuiltLanes {
  if (!run.started_at) {
    return { lanes: [], totalMs: 0, firstTestStartMs: 0, hasCrossNode: false };
  }
  const runStart = new Date(run.started_at).getTime();
  const nowMs = Date.now();
  const runEnd = run.completed_at ? new Date(run.completed_at).getTime() : nowMs;

  const clusterNodes = run.cluster_info?.nodes ?? [];
  const orderedNodeNames = clusterNodes.map((node) => node.name).filter(Boolean);
  const laneMap = new Map<string, TestBar[]>();
  for (const name of orderedNodeNames) {
    laneMap.set(name, []);
  }
  const clusterFallback = orderedNodeNames;

  // First pass: compute absolute startMs/durationMs per result, track maxEndMs
  // so totalMs anchors to the latest bar end (like machine-timeline.tsx). Anchoring
  // to run.completed_at / nowMs causes every-10s flicker on in-progress runs.
  type PendingBar = {
    result: CiTestResult;
    startMs: number;
    durationMs: number;
    targetNodes: string[];
  };
  const pending: PendingBar[] = [];
  let cumulativeMs = 0;
  let maxEndMs = 0;
  let firstTestStartMs = Infinity;
  let hasCrossNode = false;
  for (const result of run.test_results) {
    if (result.status === "skip") continue;
    const targetNodes = nodesForResult(result, clusterFallback);
    if (targetNodes.length === 0) continue;

    let startMs: number;
    let durationMs: number;
    if (result.started_at) {
      const resultStart = new Date(result.started_at).getTime();
      startMs = Math.max(resultStart - runStart, 0);
      let endTimestamp: number;
      if (result.completed_at) {
        endTimestamp = new Date(result.completed_at).getTime();
      } else if (result.duration_seconds) {
        endTimestamp = resultStart + result.duration_seconds * 1000;
      } else {
        // In-progress with no duration yet — extend to "now" so the bar reflects elapsed time.
        endTimestamp = nowMs;
      }
      durationMs = Math.max(endTimestamp - resultStart, 500);
    } else {
      // No `started_at` — lay out sequentially off `cumulativeMs`. Enforce a
      // generous floor (3s) and gap (500ms) so zero-duration tests (webhook)
      // don't overlap their neighbors and collapse into a single visual bar.
      startMs = cumulativeMs;
      durationMs = Math.max((result.duration_seconds || 0) * 1000, 3000);
    }
    cumulativeMs = startMs + durationMs + (result.started_at ? 200 : 500);
    maxEndMs = Math.max(maxEndMs, startMs + durationMs);
    firstTestStartMs = Math.min(firstTestStartMs, startMs);
    if (isCrossNodeResult(result)) hasCrossNode = true;
    pending.push({ result, startMs, durationMs, targetNodes });
  }

  if (pending.length === 0) {
    return { lanes: [], totalMs: 0, firstTestStartMs: 0, hasCrossNode: false };
  }

  // Clamp totalMs to [maxEndMs, runEnd - runStart]: use maxEndMs when it exceeds
  // the run window (common for in-progress runs where bars extend to nowMs).
  const totalMs = Math.max(maxEndMs, Math.max(runEnd - runStart, 1));

  for (const entry of pending) {
    const clampedStart = Math.min(entry.startMs, totalMs);
    const clampedDuration = Math.max(Math.min(entry.durationMs, totalMs - clampedStart), 0);

    for (const nodeName of entry.targetNodes) {
      if (!laneMap.has(nodeName)) {
        laneMap.set(nodeName, []);
      }
      const lane = laneMap.get(nodeName);
      if (!lane) continue;
      const perLane = barRangeForNode(
        entry.result,
        nodeName,
        clampedStart,
        clampedDuration,
        orderedNodeNames,
      );
      const laneStart = Math.min(perLane.startMs, totalMs);
      const laneDuration = Math.max(Math.min(perLane.durationMs, totalMs - laneStart), 0);
      const laneLeft = (laneStart / totalMs) * 100;
      const laneWidth = Math.min(Math.max((laneDuration / totalMs) * 100, 0.6), 100 - laneLeft);
      lane.push({
        result: entry.result,
        leftPercent: laneLeft,
        widthPercent: laneWidth,
        startMs: laneStart,
        laneRole: perLane.laneRole,
      });
    }
  }

  const lanes: NodeLane[] = [];
  for (const nodeName of laneMap.keys()) {
    const bars = laneMap.get(nodeName) ?? [];
    if (bars.length === 0) continue;
    bars.sort((first, second) => first.startMs - second.startMs);
    lanes.push({ nodeName, bars });
  }
  return {
    lanes,
    totalMs,
    firstTestStartMs: Number.isFinite(firstTestStartMs) ? firstTestStartMs : 0,
    hasCrossNode,
  };
}

/**
 * Nice, human-friendly tick interval in ms. Targets ~`targetCount` ticks over
 * `totalMs` and snaps to the nearest common human interval (1/2/5/10/15/30s,
 * 1/2/5/10/30m, 1h). Zooming raises targetCount so the axis gains detail as
 * it gets wider pixel-wise.
 */
function niceTickInterval(totalMs: number, targetCount: number): number {
  if (totalMs <= 0) return 1000;
  const rough = totalMs / Math.max(2, targetCount);
  const candidates = [
    1_000,
    2_000,
    5_000,
    10_000,
    15_000,
    30_000,
    60_000,
    2 * 60_000,
    5 * 60_000,
    10 * 60_000,
    30 * 60_000,
    60 * 60_000,
  ];
  return candidates.find((ms) => ms >= rough) ?? 60 * 60_000;
}

function buildTicks(totalMs: number, interval: number): number[] {
  const ticks: number[] = [];
  for (let ms = 0; ms <= totalMs; ms += interval) ticks.push(ms);
  // Always pin the final edge so the scale reads all the way to the end.
  if (ticks[ticks.length - 1] !== totalMs && totalMs > 0) ticks.push(totalMs);
  return ticks;
}

interface LegendItemProps {
  swatchClass: string;
  label: string;
}

function LegendItem({ swatchClass, label }: LegendItemProps) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("inline-block size-2.5 rounded-[2px]", swatchClass)} aria-hidden />
      <span>{label}</span>
    </span>
  );
}

function formatMs(milliseconds: number | null | undefined): string {
  if (milliseconds == null) return "—";
  if (milliseconds < 1000) return `${milliseconds}ms`;
  return `${(milliseconds / 1000).toFixed(milliseconds < 10_000 ? 2 : 1)}s`;
}

function formatSec(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  return `${seconds.toFixed(seconds < 10 ? 2 : 1)}s`;
}

/**
 * Rich hover tooltip for a Gantt bar — shows the full test context since
 * matrix-expanded names and cross-node operations often overflow the bar
 * width. Groups: identity, matrix axes (if any), vLLM config (if any),
 * timing, error (if any).
 */
function BarTooltipContent({
  result,
  laneRole,
}: {
  result: CiTestResult;
  laneRole: NodeTimelineLaneRole;
}) {
  const { formatTimestamp } = useTimezone();
  const axisEntries = Object.entries(result.matrix_axis_values ?? {});
  const source = result.nodes?.source;
  const restore = result.nodes?.restore;
  const multi = result.nodes?.multi ?? [];
  const statusLabel = (result.status as string).toUpperCase();
  // Background-filled badge: far more legible than colored text against the
  // card surface. Match the shape of the "pass"/"fail" chips in the results
  // table below the timeline.
  const statusBadgeClass =
    result.status === "pass"
      ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
      : result.status === "fail"
        ? "bg-rose-500/15 text-rose-400 border border-rose-500/30"
        : result.status === "skip"
          ? "bg-muted-foreground/10 text-muted-foreground border border-muted-foreground/20"
          : "bg-amber-500/15 text-amber-400 border border-amber-500/30";

  const timing: Array<[string, string]> = [];
  if (result.criu_dump_ms != null)
    timing.push(["Checkpoint (criu dump)", formatMs(result.criu_dump_ms)]);
  if (result.total_restore_ms != null)
    timing.push(["Total restore", formatMs(result.total_restore_ms)]);
  if (result.criu_restore_ms != null)
    timing.push(["Agent restore", formatMs(result.criu_restore_ms)]);
  if (result.criu_binary_duration_ms != null)
    timing.push(["CRIU binary", formatMs(result.criu_binary_duration_ms)]);
  if (result.kubernetes_overhead_ms != null)
    timing.push(["K8s overhead", formatMs(result.kubernetes_overhead_ms)]);
  if (result.mooncake_transfer_ms != null)
    timing.push(["Mooncake transfer", formatMs(result.mooncake_transfer_ms)]);
  else if (result.transfer_seconds != null)
    timing.push(["Transfer", formatSec(result.transfer_seconds)]);
  if (result.cold_start_ms != null) timing.push(["Cold start", formatMs(result.cold_start_ms)]);
  if (result.wake_up_ms != null) timing.push(["Wake up", formatMs(result.wake_up_ms)]);
  if (result.http_ready_ms != null) timing.push(["HTTP ready", formatMs(result.http_ready_ms)]);
  if (result.inference_ms != null) timing.push(["Inference", formatMs(result.inference_ms)]);

  return (
    <div className="max-w-[28rem] space-y-2 text-[11px]">
      <div className="flex items-start justify-between gap-3">
        <div className="font-mono text-[12px] font-semibold text-foreground">
          {result.test_name}
        </div>
        <span
          className={cn(
            "shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider",
            statusBadgeClass,
          )}
        >
          {statusLabel}
        </span>
      </div>
      <div className="text-muted-foreground">
        <span className="font-mono">{result.operation}</span>
        <span className="mx-1.5">·</span>
        <span className="font-mono">{result.version}</span>
        {laneRole !== "single" && (
          <>
            <span className="mx-1.5">·</span>
            <span className="capitalize">{laneRole} phase</span>
          </>
        )}
      </div>

      {(source || restore || multi.length > 0) && (
        <div className="text-muted-foreground">
          <span className="font-medium text-foreground">Nodes: </span>
          <span className="font-mono">
            {multi.length > 0
              ? `${source ?? "?"} → [${multi.join(", ")}]`
              : source && restore
                ? `${source} → ${restore}`
                : (source ?? restore ?? "—")}
          </span>
        </div>
      )}

      {(result.started_at || result.completed_at) && (
        <div>
          <div className="mb-0.5 font-medium text-foreground">When</div>
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 font-mono text-[10.5px] text-muted-foreground">
            {result.started_at && (
              <>
                <span>started</span>
                <span className="text-foreground">{formatTimestamp(result.started_at)}</span>
              </>
            )}
            {result.completed_at && (
              <>
                <span>completed</span>
                <span className="text-foreground">{formatTimestamp(result.completed_at)}</span>
              </>
            )}
          </div>
        </div>
      )}

      {axisEntries.length > 0 && (
        <div>
          <div className="mb-0.5 font-medium text-foreground">Matrix</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-[10.5px]">
            {axisEntries.map(([key, value]) => (
              <div key={key} className="truncate text-muted-foreground">
                <span>{key}</span>
                <span className="mx-1">=</span>
                <span className="text-foreground">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(result.model_id || result.tensor_parallel != null || result.quantization) && (
        <div>
          <div className="mb-0.5 font-medium text-foreground">Engine config</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">
            {result.model_id && <span>model={result.model_id} </span>}
            {result.tensor_parallel != null && <span>tp={result.tensor_parallel} </span>}
            {result.data_parallel != null && <span>dp={result.data_parallel} </span>}
            {result.expert_parallel === true && <span>ep=true </span>}
            {result.quantization && <span>quant={result.quantization}</span>}
          </div>
        </div>
      )}

      {timing.length > 0 && (
        <div>
          <div className="mb-0.5 font-medium text-foreground">Timing</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-[10.5px]">
            {timing.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-2">
                <span className="truncate text-muted-foreground">{label}</span>
                <span className="shrink-0 text-foreground">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {result.restore_method && (
        <div className="text-muted-foreground">
          <span className="font-medium text-foreground">Restore method: </span>
          <span className="font-mono">{result.restore_method}</span>
        </div>
      )}

      {result.error && (
        <div>
          <div className="mb-0.5 font-medium text-rose-400">
            Failed{result.failure_phase ? ` @ ${result.failure_phase}` : ""}
          </div>
          <div className="whitespace-pre-wrap break-words font-mono text-[10.5px] text-muted-foreground">
            {result.error}
          </div>
        </div>
      )}
    </div>
  );
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 16;

export function NodeTimeline({ run }: { run: CiRunDetail }) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { lanes, totalMs, firstTestStartMs, hasCrossNode } = useMemo(() => buildLanes(run), [run]);

  // Cmd/Ctrl + wheel zooms the timeline, anchored under the cursor (Google-
  // Maps / Chrome-DevTools style): the content point under the cursor stays
  // under the cursor after zoom. Without this, every zoom jumps the view to
  // the start.
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const handler = (event: WheelEvent) => {
      if (!event.metaKey && !event.ctrlKey) return;
      event.preventDefault();
      const rect = node.getBoundingClientRect();
      const cursorX = event.clientX - rect.left;
      const contentXBefore = node.scrollLeft + cursorX;
      const widthBefore = node.scrollWidth || rect.width;
      const factor = Math.exp(-event.deltaY * 0.002);

      setZoom((previous) => {
        const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, previous * factor));
        if (next === previous) return previous;
        const ratio = widthBefore > 0 ? contentXBefore / widthBefore : 0;
        // Wait one frame for the inner wrapper to resize, then realign scroll.
        requestAnimationFrame(() => {
          const widthAfter = node.scrollWidth;
          const contentXAfter = ratio * widthAfter;
          node.scrollLeft = contentXAfter - cursorX;
        });
        return next;
      });
    };
    node.addEventListener("wheel", handler, { passive: false });
    return () => node.removeEventListener("wheel", handler);
  }, []);

  if (lanes.length === 0) return null;

  // Dead-time overlay: width as % of the run window; gate on a meaningful gap
  // so single-test-at-run-start runs don't render a zero-width sliver.
  const deadTimePercent =
    firstTestStartMs > 0 && firstTestStartMs < totalMs ? (firstTestStartMs / totalMs) * 100 : 0;

  const tickTarget = Math.min(40, Math.max(6, Math.round(8 * zoom)));
  const tickInterval = niceTickInterval(totalMs, tickTarget);
  const ticks = buildTicks(totalMs, tickInterval);

  const zoomPercent = zoom * 100;

  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-accent/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          {collapsed ? (
            <ChevronRight className="size-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="size-4 text-muted-foreground" />
          )}
          <span className="text-sm font-semibold">Node timeline</span>
          <span className="text-xs text-muted-foreground">
            {lanes.length} node{lanes.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {zoom > 1.01 && (
            <>
              <span className="font-mono text-[11px] text-muted-foreground">
                {zoom.toFixed(zoom < 10 ? 1 : 0)}×
              </span>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setZoom(1);
                }}
                className="flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors"
                aria-label="Reset zoom"
              >
                <RotateCcw className="size-3" />
                reset
              </button>
            </>
          )}
          <span className="font-mono text-xs text-muted-foreground">
            {formatDurationMs(totalMs)}
          </span>
        </div>
      </button>
      {!collapsed && (
        <div className="border-t border-border p-4">
          <div className="flex gap-3">
            {/* Lane labels stay static outside the scroller. */}
            <div className="w-48 shrink-0 space-y-2">
              {lanes.map((lane) => {
                const isUnassigned = lane.nodeName === UNASSIGNED_LANE;
                const label = isUnassigned ? UNASSIGNED_LANE_LABEL : lane.nodeName;
                return (
                  <div
                    key={lane.nodeName}
                    className={cn(
                      "h-6 truncate font-mono text-[12px] leading-6",
                      isUnassigned ? "italic text-muted-foreground/70" : "text-muted-foreground",
                    )}
                    title={
                      isUnassigned
                        ? "Tests that didn't specify a node — K3s scheduled them on whichever node had capacity."
                        : lane.nodeName
                    }
                  >
                    {label}
                  </div>
                );
              })}
              {/* spacer aligned with the tick-axis row below */}
              <div className="h-5" aria-hidden />
            </div>
            {/* Scrolling container — Cmd/Ctrl + wheel zooms; plain wheel scrolls.
                Slim webkit scrollbar sits flush below the tick axis. */}
            <div
              ref={scrollRef}
              className={cn(
                "min-w-0 flex-1 overflow-x-auto overflow-y-hidden rounded",
                "[&::-webkit-scrollbar]:h-1",
                "[&::-webkit-scrollbar-track]:bg-transparent",
                "[&::-webkit-scrollbar-thumb]:rounded-full",
                "[&::-webkit-scrollbar-thumb]:bg-muted-foreground/30",
                "hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/50",
              )}
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(120,120,120,0.35) transparent",
              }}
            >
              <div style={{ width: `${zoomPercent}%`, minWidth: "100%" }}>
                <div className="space-y-2">
                  {lanes.map((lane) => (
                    <div key={lane.nodeName} className="relative h-6 rounded bg-muted/40">
                      {deadTimePercent > 0 && (
                        <Tooltip>
                          <TooltipTrigger
                            render={<div />}
                            aria-hidden
                            className="absolute inset-y-0 left-0 rounded-l pointer-events-auto"
                            style={{
                              width: `${deadTimePercent}%`,
                              backgroundImage:
                                "repeating-linear-gradient(135deg, transparent 0, transparent 4px, hsl(var(--muted-foreground) / 0.18) 4px, hsl(var(--muted-foreground) / 0.18) 5px)",
                            }}
                          />
                          <TooltipContent className="border-2 border-border bg-card text-foreground">
                            Pre-test setup: {formatDurationMs(firstTestStartMs)} — cluster
                            detection, preflight image pull, namespace clean, first pod Ready,
                            checkpoint wait.
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {lane.bars.map((bar) => {
                        // Zoom inflates the inner wrapper, so percent widths
                        // auto-scale. In-bar label threshold scales with zoom
                        // so short bars get labels once they're wide enough.
                        const effectivePercent = bar.widthPercent * zoom;
                        return (
                          <Tooltip key={`${bar.result.id}-${lane.nodeName}`}>
                            <TooltipTrigger
                              onClick={() => navigate(`/ci/${run.run_id}/tests/${bar.result.id}`)}
                              aria-label={`${bar.result.test_name} (${bar.result.status})`}
                              className={cn(
                                "absolute top-0.5 h-5 rounded transition-all hover:brightness-110",
                                "flex items-center justify-start overflow-hidden",
                                "px-1.5 font-mono text-[10px] text-white/95",
                                nodeTimelineBarColor(bar.laneRole, bar.result.status),
                                (bar.result.status as string) === "in_progress" && "animate-pulse",
                              )}
                              style={{
                                left: `${bar.leftPercent}%`,
                                width: `${bar.widthPercent}%`,
                                minWidth: "3px",
                              }}
                            >
                              {effectivePercent >= 5 && (
                                <span className="truncate whitespace-nowrap">
                                  {bar.result.test_name}
                                </span>
                              )}
                            </TooltipTrigger>
                            <TooltipContent
                              side="bottom"
                              sideOffset={6}
                              className="max-w-[28rem] border-2 border-border bg-card text-foreground"
                            >
                              <BarTooltipContent result={bar.result} laneRole={bar.laneRole} />
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  ))}
                  {/* Tick axis — same coordinate space as the lanes.
                      Bottom padding (mb-3) pushes the webkit scrollbar away
                      from the axis labels so they don't overlap. */}
                  <div className="relative h-5 mb-3 border-t border-border" aria-hidden>
                    {ticks.map((ms) => {
                      const leftPercent = (ms / totalMs) * 100;
                      const isLast = ms === totalMs && ticks.length > 1;
                      return (
                        <div
                          key={ms}
                          className="absolute top-0"
                          style={{ left: `${leftPercent}%` }}
                        >
                          <div className="h-1.5 w-px bg-border" />
                          <span
                            className={cn(
                              "absolute top-2 font-mono text-[10px] text-muted-foreground whitespace-nowrap",
                              isLast ? "right-0 -translate-x-0" : "-translate-x-1/2",
                              ms === 0 && "translate-x-0",
                            )}
                          >
                            {formatDurationMs(ms)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {hasCrossNode && (
            <div className="mt-6 flex flex-wrap items-center gap-4 pl-[calc(12rem+0.75rem)] text-[11px] text-muted-foreground">
              <LegendItem swatchClass={NODE_TIMELINE_COLORS.source} label="Source (checkpoint)" />
              <LegendItem
                swatchClass={NODE_TIMELINE_COLORS.restore}
                label="Restore (from checkpoint)"
              />
              <LegendItem swatchClass={NODE_TIMELINE_COLORS.single} label="Single-node" />
              <LegendItem swatchClass={NODE_TIMELINE_COLORS.fail} label="Failed" />
              <LegendItem swatchClass={NODE_TIMELINE_COLORS.skip} label="Skipped" />
              <LegendItem
                swatchClass="bg-[repeating-linear-gradient(135deg,transparent_0,transparent_3px,hsl(var(--muted-foreground)/0.35)_3px,hsl(var(--muted-foreground)/0.35)_4px)]"
                label="Pre-test setup"
              />
              <span className="ml-auto font-mono text-[10px]">⌘ + scroll to zoom</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
